const MAX_DIAS_PERIODO = 366

/**
 * Formata um valor de tempo vindo do contrato (sempre em `unidadeMedida`, hoje
 * "minutos"). Devolve `null` quando o valor é ausente — quem renderiza decide o
 * texto de "sem dados"; nunca devolve "0 min" para ausência de dado.
 * O sinal é preservado: atraso negativo significa que o atendimento começou
 * antes da vaga, e isso é informação, não erro.
 */
export const formatarMinutos = (valor: number | null, unidadeMedida: string): string | null => {
  if (valor === null || valor === undefined || Number.isNaN(valor)) return null

  const numero = new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(valor)

  return unidadeMedida ? `${numero} ${unidadeMedida}` : numero
}

const MINUTOS_POR_HORA = 60
const MINUTOS_POR_DIA = 24 * MINUTOS_POR_HORA

/**
 * Tempo do contrato numa leitura humana, com a unidade escolhida pela grandeza
 * do valor: segundos (`45 s`), minutos com segundos abaixo de 10 min (`4 min 30 s`),
 * minutos (`45 min`), horas e minutos (`3 h 20 min`) e dias e horas (`2 dias e 4 h`).
 *
 * É só apresentação: o valor calculado pelo servidor não muda, e o arredondamento
 * acontece uma única vez, na menor unidade exibida. O sinal é preservado (atraso
 * negativo = começou antes da vaga). Se o payload vier numa unidade que não seja
 * minutos, cai no `formatarMinutos`, que escreve o número com a unidade recebida.
 */
export const formatarDuracao = (valor: number | null, unidadeMedida: string): string | null => {
  if (valor === null || valor === undefined || Number.isNaN(valor)) return null
  if (unidadeMedida?.toLowerCase() !== 'minutos') return formatarMinutos(valor, unidadeMedida)

  const sinal = valor < 0 ? '-' : ''
  const segundosTotais = Math.round(Math.abs(valor) * 60)

  if (segundosTotais < 60) return `${sinal}${segundosTotais} s`

  if (segundosTotais < 10 * 60) {
    const minutos = Math.floor(segundosTotais / 60)
    const segundos = segundosTotais % 60
    return `${sinal}${minutos} min${segundos > 0 ? ` ${segundos} s` : ''}`
  }

  const minutosTotais = Math.round(segundosTotais / 60)

  if (minutosTotais < MINUTOS_POR_HORA) return `${sinal}${minutosTotais} min`

  if (minutosTotais < MINUTOS_POR_DIA) {
    const horas = Math.floor(minutosTotais / MINUTOS_POR_HORA)
    const minutos = minutosTotais % MINUTOS_POR_HORA
    return `${sinal}${horas} h${minutos > 0 ? ` ${minutos} min` : ''}`
  }

  let dias = Math.floor(minutosTotais / MINUTOS_POR_DIA)
  let horas = Math.round((minutosTotais % MINUTOS_POR_DIA) / MINUTOS_POR_HORA)
  if (horas === 24) {
    dias += 1
    horas = 0
  }

  const rotuloDias = `${formatarContagem(dias)} ${dias === 1 ? 'dia' : 'dias'}`
  return `${sinal}${rotuloDias}${horas > 0 ? ` e ${horas} h` : ''}`
}

/** Contagem inteira (denominadores e totais) no formato pt-BR. */
export const formatarContagem = (valor: number): string =>
  new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(valor)

/**
 * Fração 0..1 do contrato exibida em percentual (`0.32` → `32,0%`). Ausência de
 * dado (denominador zero) devolve `null` — quem renderiza escreve "sem dados";
 * nunca "0%", que seria medição feita com resultado zero.
 */
export const formatarPercentual = (valor: number | null | undefined): string | null => {
  if (valor === null || valor === undefined || Number.isNaN(valor)) return null

  return new Intl.NumberFormat('pt-BR', {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(valor)
}

/**
 * NPS do contrato em fração [-1, 1] exibido na escala usual −100..+100
 * (`0.8854` → `+89`). É transformação de exibição, não recálculo: o valor bruto
 * não é recombinado com nenhum outro campo, e a escala é dita no rótulo da tela
 * para ninguém ler o número como percentual.
 */
export const formatarNps = (valor: number | null | undefined): string | null => {
  if (valor === null || valor === undefined || Number.isNaN(valor)) return null

  return new Intl.NumberFormat('pt-BR', {
    maximumFractionDigits: 0,
    signDisplay: 'always',
  }).format(valor * 100)
}

/** Nota da escala 0–5 com uma casa; `null` quando não há resposta válida. */
export const formatarNota = (valor: number | null | undefined): string | null => {
  if (valor === null || valor === undefined || Number.isNaN(valor)) return null

  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(valor)
}

/**
 * Janela da heurística de suspeita, em segundos no contrato, exibida na unidade
 * que o valor permite (`60` → "1 min", `90` → "90 s"). A unidade vem do `Intl`,
 * não de texto fixo no código.
 */
export const formatarSegundos = (segundos: number): string => {
  const emMinutos = segundos >= 60 && segundos % 60 === 0

  return new Intl.NumberFormat('pt-BR', {
    style: 'unit',
    unit: emMinutos ? 'minute' : 'second',
    unitDisplay: 'short',
    maximumFractionDigits: 0,
  }).format(emMinutos ? segundos / 60 : segundos)
}

export { MAX_DIAS_PERIODO }

const formatoUmaCasa = (extra: Intl.NumberFormatOptions = {}) =>
  new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1, ...extra })

/**
 * Taxa já em percentual (0..100), como vem da família de performance
 * (`20` → `20,0%`). Não é `formatarPercentual`, que espera fração 0..1.
 */
export const formatarTaxaPercentual = (valor: number | null | undefined): string | null => {
  if (valor === null || valor === undefined || Number.isNaN(valor)) return null

  return `${formatoUmaCasa().format(valor)}%`
}

/**
 * Variação contra o período anterior, com sinal: `PERCENTUAL` → `+8,8%`,
 * `PONTO_PERCENTUAL` → `-2,0 p.p.` (o sufixo chega do i18n).
 */
export const formatarVariacao = (
  valor: number | null | undefined,
  tipo: 'PERCENTUAL' | 'PONTO_PERCENTUAL',
  sufixoPontoPercentual: string
): string | null => {
  if (valor === null || valor === undefined || Number.isNaN(valor)) return null

  const numero = formatoUmaCasa({ signDisplay: 'exceptZero' }).format(valor)
  return tipo === 'PERCENTUAL' ? `${numero}%` : `${numero} ${sufixoPontoPercentual}`
}
